<script setup lang="ts">
  import '@fontsource/bricolage-grotesque/700.css';
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import { listJoin } from '@/domain/store-setup/display-names';
  import type { IOrdersPreviewModel } from '@/domain/store-setup/orders-setup';
  import { renderTextRef } from '@/domain/store-setup/setup-flow';
  import type { ITextRef } from '@/domain/store-setup/setup-flow';
  import { previewColors } from '@/domain/storefront/storefront-colors';

  /**
   * «Así lo verán sus clientes» (plan §4.2 item 7, §4.8 item 5; Storefront V2
   * D-ADM-5): a small, non-interactive phone showing the storefront's
   * generated cover in the Moderno style, light mode (DESIGN.md section 3
   * R1/R2), drawn from `brand`. The `store` variant adds skeleton rows; the
   * `orders` variant shows how diners choose how to order. Not an iframe: the
   * store may not exist yet. Decorative for assistive technology (the form
   * next to it holds the same data).
   */
  const props = withDefaults(
    defineProps<{
      variant: 'store' | 'orders';
      /** The store colour «#rrggbb» the cover is drawn from (D-ADM-5). */
      brand: string;
      name: string;
      address: string;
      logoUrl?: string | null;
      initials: string;
      /** «12:00–15:30 · 19:30–00:00», or null when the store is closed today or has no hours. */
      todayHours?: string | null;
      orders?: IOrdersPreviewModel | null;
    }>(),
    { logoUrl: null, todayHours: null, orders: null }
  );
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  /** The image failed to load: the seal stands in, like on the storefront. */
  const logoFailed = ref(false);

  const locale = computed(() => proxy.$i18n.locale);
  const displayName = computed(
    () => props.name.trim() || tr('storeSetup.preview.placeholderName')
  );
  const todayLine = computed(() =>
    props.todayHours
      ? tr('storeSetup.preview.today', { hours: props.todayHours })
      : null
  );
  const showLogo = computed(() => !!props.logoUrl && !logoFailed.value);

  /** The storefront's colours for this brand; they reach the DOM only as variables. */
  const paletteStyle = computed(() => {
    const palette = previewColors(props.brand);
    return {
      '--pv-paper': palette.paper,
      '--pv-surface': palette.surface,
      '--pv-ink': palette.ink,
      '--pv-ink-2': palette.ink2,
      '--pv-cover': palette.cover,
      '--pv-on-cover': palette.onCover,
      '--pv-on-cover-2': palette.onCover2,
      '--pv-cover-orn': palette.coverOrn,
    };
  });

  const initialChars = computed(() => Array.from(props.initials));
  /** Moderno's «initial» ornament: the first initial, outlined (none for «?»). */
  const ornament = computed(() =>
    props.initials === '?' ? '' : initialChars.value[0] ?? ''
  );
  /** The prototype's small seal (`final/app.js:264-265`). */
  const sealY = computed(() => (initialChars.value.length > 1 ? 63 : 66));
  const sealStyle = computed(() => ({
    fontSize: `${initialChars.value.length > 1 ? 40 : 52}px`,
  }));
  /** Longer names get a smaller face, so they fit in two lines. */
  const nameStyle = computed(() => {
    const length = Array.from(displayName.value.trim()).length;
    let size = 20;
    if (length <= 9) size = 34;
    else if (length <= 14) size = 28;
    else if (length <= 20) size = 24;
    return { fontSize: `${size}px` };
  });

  const payWith = computed(() => {
    const keys = props.orders?.paymentKeys ?? [];
    if (!keys.length) return null;
    return tr('storeSetup.orders.preview.payWith', {
      methods: listJoin(
        keys.map((key) => tr(key)),
        locale.value
      ),
    });
  });

  function lineText(lines: ITextRef[]): string {
    return lines
      .map((line) =>
        renderTextRef(
          line,
          (key, params, count) =>
            count === undefined
              ? tr(key, params)
              : String(proxy.$tc(key, count, params)),
          locale.value
        )
      )
      .join(' · ');
  }

  watch(
    () => props.logoUrl,
    () => {
      logoFailed.value = false;
    }
  );
</script>

<template>
  <div class="v2-setup-store-preview" aria-hidden="true">
    <p class="preview-caption">{{ $t('storeSetup.preview.caption') }}</p>
    <div class="phone-frame">
      <div class="phone-screen" :style="paletteStyle">
        <div class="store-cover">
          <span v-if="ornament" class="cover-ornament">{{ ornament }}</span>
          <div class="cover-top">
            <span class="cover-mark">
              <img
                v-if="showLogo"
                class="logo-image"
                :src="logoUrl"
                alt=""
                @error="logoFailed = true"
              />
              <svg v-else class="cover-seal" viewBox="0 0 100 100">
                <circle class="seal-ring" cx="50" cy="50" r="46" />
                <text
                  class="seal-text"
                  x="50"
                  :y="sealY"
                  text-anchor="middle"
                  :style="sealStyle"
                >
                  {{ initials }}
                </text>
              </svg>
            </span>
            <span v-if="address.trim()" class="cover-kicker">{{
              address
            }}</span>
            <span class="cover-info">
              <i class="mdi mdi-information-variant" />
            </span>
          </div>
          <p class="store-name" :style="nameStyle">{{ displayName }}</p>
          <p v-if="todayLine" class="cover-status">{{ todayLine }}</p>
        </div>
        <div v-if="variant === 'store'" class="store-card">
          <div v-for="row in 3" :key="row" class="skeleton-row">
            <span class="skeleton-name" />
            <span class="skeleton-price" />
          </div>
        </div>
        <div v-else-if="orders" class="store-card order-choice">
          <p v-if="orders.menuOnly" class="choice-note">
            {{ $t('storeSetup.orders.preview.menuOnly') }}
          </p>
          <template v-else>
            <p class="choice-title">
              {{ $t('storeSetup.orders.preview.question') }}
            </p>
            <div v-for="row in orders.rows" :key="row.type" class="choice-row">
              <span class="row-title">{{ $t(row.titleKey) }}</span>
              <span v-if="row.lines.length" class="row-lines">{{
                lineText(row.lines)
              }}</span>
            </div>
            <p v-if="payWith" class="choice-note">{{ payWith }}</p>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-setup-store-preview {
    @apply tw-pointer-events-none tw-flex tw-w-full tw-select-none tw-flex-col;
    .preview-caption {
      @apply tw-m-0 tw-mb-2 tw-ml-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral-500;
    }
    .phone-frame {
      @apply tw-rounded-[28px] tw-bg-neutral-900 tw-p-2.5;
    }
    // Every store colour is a variable set from the brand (paletteStyle).
    .phone-screen {
      @apply tw-flex tw-flex-col tw-overflow-hidden tw-rounded-[20px] tw-pb-4;
      background-color: var(--pv-paper);
      min-height: 24rem;
    }
    .store-cover {
      @apply tw-relative tw-shrink-0 tw-overflow-hidden tw-px-4 tw-pb-4 tw-pt-3;
      background-color: var(--pv-cover);
      color: var(--pv-on-cover);
    }
    // Moderno's «initial» ornament (final/app.css:80), behind the text.
    .cover-ornament {
      position: absolute;
      right: 4px;
      bottom: -0.24em;
      font-family: 'Bricolage Grotesque', sans-serif;
      font-weight: 700;
      font-size: 150px;
      line-height: 1;
      color: transparent;
      -webkit-text-stroke: 2px var(--pv-on-cover);
      opacity: 0.24;
    }
    .cover-top {
      @apply tw-relative tw-flex tw-items-center;
    }
    .cover-mark {
      @apply tw-flex tw-h-11 tw-w-11 tw-flex-none;
    }
    .logo-image {
      @apply tw-block tw-h-11 tw-w-11 tw-rounded-lg tw-object-cover;
      background-color: var(--pv-surface);
    }
    .cover-seal {
      @apply tw-block tw-h-11 tw-w-11;
      overflow: visible;
    }
    .seal-ring {
      fill: none;
      stroke: currentColor;
      stroke-width: 3;
    }
    .seal-text {
      fill: currentColor;
      font-family: 'Bricolage Grotesque', sans-serif;
      font-weight: 700;
    }
    .cover-kicker {
      @apply tw-ml-3 tw-mr-2 tw-min-w-0 tw-flex-1 tw-truncate tw-font-bold tw-uppercase;
      font-size: 10px;
      letter-spacing: 0.14em;
      color: var(--pv-on-cover-2);
    }
    .cover-info {
      @apply tw-ml-auto tw-flex tw-h-7 tw-w-7 tw-flex-none tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-text-base tw-leading-none;
      border-color: var(--pv-on-cover-2);
    }
    .store-name {
      @apply tw-relative tw-m-0 tw-mt-3 tw-leading-none;
      font-family: 'Bricolage Grotesque', sans-serif;
      font-weight: 700;
      letter-spacing: -0.03em;
      overflow-wrap: anywhere;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .cover-status {
      @apply tw-relative tw-m-0 tw-mt-2 tw-text-xs;
      color: var(--pv-on-cover-2);
    }
    .store-card {
      @apply tw-mx-3 tw-mt-3 tw-rounded-xl tw-px-3 tw-py-1;
      background-color: var(--pv-surface);
    }
    .skeleton-row {
      @apply tw-flex tw-items-center tw-justify-between tw-py-2.5;
    }
    .skeleton-name,
    .skeleton-price {
      @apply tw-block tw-h-2.5 tw-rounded-md;
      background-color: var(--pv-paper);
    }
    .skeleton-name {
      width: 60%;
    }
    .skeleton-price {
      width: 18%;
    }
    .order-choice {
      @apply tw-space-y-2 tw-py-3;
    }
    .choice-title {
      @apply tw-m-0 tw-text-sm tw-font-bold;
      color: var(--pv-ink);
    }
    .choice-row {
      @apply tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-px-3 tw-py-2 tw-text-xs;
    }
    .row-title {
      @apply tw-shrink-0 tw-font-medium;
      color: var(--pv-ink);
    }
    .row-lines {
      @apply tw-ml-2 tw-min-w-0 tw-text-right;
      font-size: 11px;
      color: var(--pv-ink-2);
    }
    .choice-note {
      @apply tw-m-0 tw-text-xs;
      color: var(--pv-ink-2);
    }
  }
</style>
